import { Check, Close } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { IsTenantCoordinatorOrAdmin } from 'shared/permissions/IsTenantCoordinator';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { productService } from 'shared/services';
import toast from 'shared/utils/toast';
import { IContractProductLine } from 'features/inventory/contracts/types';
import { DataGridCell, baseCell } from 'components/cells';
import listCell from 'components/cells/list-cell';
import {
  filterOperators,
  booleanFilterOperators,
  numberFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';
import { booleanValueFormatter } from 'components/enhanced/data-grid/utils/helpers';
import { ConfirmationUnlinkTableDialog } from 'components/ui/feedback';
import FileUploader from 'components/ui/file-uploader/FileUploader';

export function ProductsColumns(
  types: string[],
  onUploadSuccess: any,
  sap_id = false,
  detailNav = false
): GridColDef[] {
  const { t } = useTranslation();
  const translationPrefix = 'entities.product';
  const columns: GridColDef[] = [
    {
      field: 'code',
      headerName: t(`global.code`),
      filterOperators,
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const product = params.row;
        return detailNav ? (
          <Link to={`${product.id}`}>
            <b className="text-secondary-500">{product.code}</b>
          </Link>
        ) : (
          <DataGridCell content={params.value as string} />
        );
      }
    }
  ];

  if (sap_id) {
    columns.push({
      field: 'sap_id',
      headerName: t(`global.sap_id`),
      filterOperators,
      width: 100,
      renderCell: baseCell
    });
  }

  if (IsSuperAdminGuard()) {
    columns.push({
      field: 'association',
      headerName: t(`global.association`),
      width: 80,
      align: 'center',
      filterOperators: booleanFilterOperators,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          icon={params.value ? <Check color="success" /> : <Close color="error" />}
          sx={{
            background: 'transparent'
          }}
        />
      )
    });
  }

  columns.push(
    {
      field: 'commercial_name',
      headerName: t(`${translationPrefix}.commercial_name`),
      filterOperators,
      width: 350,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      filterOperators,
      width: 350,
      renderCell: baseCell
    },
    {
      field: 'ref',
      headerName: t(`${translationPrefix}.ref`),
      filterOperators,
      width: 150,
      renderCell: baseCell
    },
    {
      field: 'type',
      headerName: t(`global.type`),
      type: 'singleSelect',
      valueOptions: types,
      width: 100,
      filterOperators: singleSelectFilterOperators,
      renderCell: baseCell
    },
    {
      field: 'category',
      headerName: t(`global.category`),
      filterOperators,
      width: 100,
      renderCell: baseCell
    },
    {
      field: 'warehouses',
      headerName: t(`global.warehouses`),
      filterOperators,
      width: 200,
      valueGetter: (params) => params.value && params.value.join(', '),
      renderCell: (params: GridRenderCellParams) => {
        return <DataGridCell content={params.value} />;
      }
    },
    {
      field: 'contract_codes',
      valueGetter: (params) => params.row.contract_lines,
      headerName: t(`${translationPrefix}.contract_codes`),
      filterOperators,
      width: 200,
      valueFormatter: ({ value }) => {
        if (!Array.isArray(value) || value.length === 0) return '';
        return value
          .map((line: IContractProductLine) => line.contract_code)
          .filter(Boolean)
          .join(', ');
      },
      renderCell: (params) => <DataGridCell content={params.formattedValue} />
    },
    {
      field: 'skus',
      headerName: t(`pages.sku.entity.sku`),
      filterOperators,
      width: 200,
      renderCell: listCell
    },
    {
      field: 'unit_prices',
      valueGetter: (params) => params.row.contract_lines,
      sortable: true,
      filterOperators,
      headerName: t(`${translationPrefix}.unit_prices`),
      valueFormatter: ({ value }) => {
        if (!Array.isArray(value) || value.length === 0) return '';
        return value
          .map((line: IContractProductLine) => line.unit_price)
          .map(currencyFormatter, { significantDigits: 3 })
          .join(',');
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <IsTenantCoordinatorOrAdmin>
            <DataGridCell content={params.formattedValue} />;
          </IsTenantCoordinatorOrAdmin>
        );
      },
      width: 200
    },
    {
      field: 'suppliers',
      valueGetter: (params) => params.row.contract_lines,
      filterOperators,
      headerName: t(`${translationPrefix}.suppliers`),
      valueFormatter: ({ value }) => {
        if (!Array.isArray(value) || value.length === 0) return '';
        return value.map((line: IContractProductLine) => line.supplier).join(',');
      },
      renderCell: (params: GridRenderCellParams) => {
        return <DataGridCell content={params.formattedValue} />;
      },
      width: 300
    },
    {
      field: 'expiration_alert_threshold',
      // filterOperators: numberFilterOperators, NON FA CI VUOLE FILTRO CUSTOM
      filterable: false,
      sortable: true,
      headerName: t(`${translationPrefix}.expiration_alert_threshold`),
      type: 'number',
      width: 100,
      renderCell: (params: GridRenderCellParams<any>) => (
        <DataGridCell
          content={
            params.value &&
            t(`${translationPrefix}.expiration_alert_threshold_field`, {
              n_days: params.value
            })
          }
        />
      )
    },
    {
      field: 'temperature',
      filterOperators,
      headerName: t(`${translationPrefix}.preservation_temperature`),
      renderCell: baseCell
    },
    {
      field: 'locations',
      filterOperators,
      headerName: t(`${translationPrefix}.locations`),
      renderCell: baseCell
    },
    {
      field: 'uom',
      headerName: t(`${translationPrefix}.uom`),
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'packaging',
      headerName: t(`${translationPrefix}.packaging`),
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'tests_per_kit',
      headerName: t(`${translationPrefix}.tests_per_kit`),
      type: 'number',
      width: 100,
      sortable: true,
      filterable: true,
      filterOperators: numberFilterOperators,
      renderCell: baseCell
    },
    {
      field: 'ivdr',
      headerName: t(`${translationPrefix}.ivdr_class`),
      filterOperators,
      width: 150,
      renderCell: baseCell
    },
    {
      field: 'cnd_code',
      headerName: t(`${translationPrefix}.cnd_code`),
      filterOperators,
      width: 150,
      renderCell: baseCell
    },
    {
      field: 'rdm_code',
      headerName: t(`${translationPrefix}.rdm_code`),
      filterOperators,
      width: 150,
      renderCell: baseCell
    }
  );

  [
    'manufacturer_name',
    'manufacturer_id',
    'supplier_id',
    'ce_regulation',
    'id_issuing_authority'
  ].forEach((field) => {
    columns.push({
      field: field,
      headerName: t(`${translationPrefix}.${field}`),
      filterOperators,
      renderCell: baseCell
    });
  });

  // ['restock_threshold', 'understock_threshold', 'overstock_threshold'].forEach((threshold) => {
  //   columns.push({
  //     field: threshold,
  //     headerName: t(`${translationPrefix}.${threshold}`),
  //     type: 'number',
  //     width: 100,
  //     editable: true,
  //     filterOperators: numberFilterOperators,
  //     renderCell: baseCell
  //   });
  // });

  ['technical_file', 'safety_document', 'ifu', 'ce_ivd'].forEach((file) => {
    columns.push({
      field: file,
      headerName: t(`${translationPrefix}.${file}`),
      valueFormatter: booleanValueFormatter,
      renderCell: (params: GridRenderCellParams) => {
        const product = params.row;
        return (
          <IsTenantAdministrator>
            <div>
              <FileUploader
                onUploadSuccess={onUploadSuccess}
                service={productService}
                entityId={product.id}
                attachmentName={file}
                filename={product[file]}
              />
            </div>
          </IsTenantAdministrator>
        );
      },
      width: 70,
      filterable: false,
      sortable: false
    });
  });

  columns.push({
    field: 'actions',
    type: 'actions',
    flex: 0,
    minWidth: 80,
    width: 80,
    headerName: t(`global.actions`),
    getActions: (params: GridRowParams) => {
      const actions = [];
      actions.push(
        <IsTenantAdministrator key={123}>
          <ConfirmationUnlinkTableDialog
            id={+params.row.id}
            handleUnlink={(id) => {
              productService.purgeLinks(id).then(() => {
                toast.success(t(`global.successfully.unlinked`));
                if (onUploadSuccess) onUploadSuccess();
              });
            }}
          />
        </IsTenantAdministrator>
      );
      return actions;
    }
  } as GridColDef);

  // Nascondi completamente la colonna "Associato" per non-superadmin (mai mostrarla neanche vuota)
  const isSuperAdmin = IsSuperAdminGuard();
  return (columns as GridColDef[]).filter((col) => col.field !== 'association' || isSuperAdmin);
}
